import { Box, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useLazyGetPieceQuery, useUpdatePiecesMutation } from '@api/piece';
import { Piece, PieceAction } from '@apiContract';
import { usePiecePdf } from '@components/Mailpiece/hooks/usePiecePdf';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';
import { SCAN } from 'src/constants';

import { ActionComponentProps } from '../../../index';
import { CollapsibleOptions } from '../CollapsibleOptions';
import { DialogOpenAndScan } from './components/DialogOpenAndScan';

export const mailpieceHasContent = (mailpiece: Piece) =>
  Boolean(mailpiece.pieceDoc?.piecePages.length || mailpiece.pieceDoc?.pdfUrl);

export const OpenAndScan = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces] = useUpdatePiecesMutation();

  const mailpiece = mailpieces[0];

  const [getPiece] = useLazyGetPieceQuery();
  const { handleCreatePdfFromPages } = usePiecePdf(mailpiece.id, mailpiece.pieceDoc || null);

  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [openAndScanOpen, setOpenAndScanOpen] = useState<boolean>(false);
  const [finishOpenAndScanOpen, setFinishOpenAndScanOpen] = useState<boolean>(false);
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);
  const [openAndScanMode, setOpenAndScanMode] = useState<string>(SCAN);

  useEffect(() => {
    if (actionStarted) {
      if (openAndScanOpen || finishOpenAndScanOpen) return;
      const fn = !mailpieceHasContent(mailpiece) ? setOpenAndScanOpen : setFinishOpenAndScanOpen;
      fn(true);
    } else {
      setOpenAndScanOpen(false);
      setFinishOpenAndScanOpen(false);
    }
  }, [actionStarted, mailpiece, openAndScanOpen, finishOpenAndScanOpen]);

  const handleOpenAndScanComplete = (mode: string) => {
    setOpenAndScanMode(mode);
    setOpenAndScanOpen(false);
    setFinishOpenAndScanOpen(true);
  };

  const confirmFinishOpenAndScan = async () => {
    try {
      setActionInProgress(true);

      let latestPiece = mailpiece;

      if (!mailpiece.pieceDoc || !mailpiece.pieceDoc.piecePages.length) {
        const latestPieceResult = await getPiece(mailpiece.id);
        latestPiece = latestPieceResult.data || mailpiece;
      }

      if (latestPiece.pieceDoc && latestPiece.pieceDoc.piecePages.length > 0 && !latestPiece.pieceDoc.pdfUrl) {
        await handleCreatePdfFromPages(latestPiece.pieceDoc);
      }

      await updatePieces({
        pieces: [{ id: mailpiece.id }],
        action: PieceAction.FinishOpenAndScan,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: [mailpiece.id],
        }).unwrap();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionInProgress(false);
      onActionFinish();
    }
  };

  const isScan = openAndScanMode === SCAN;

  return (
    <>
      <DialogOpenAndScan
        mailpieces={mailpieces}
        actionStarted={openAndScanOpen}
        onActionFinish={onActionFinish}
        onOpenAndScanComplete={handleOpenAndScanComplete}
      />

      <DialogConfirmation
        open={finishOpenAndScanOpen}
        loading={actionInProgress}
        maxWidth="sm"
        title={isScan ? 'Convert and send item contents' : 'Send item contents'}
        content={
          <Box>
            <Typography variant="body1" color="text.secondary">
              {isScan
                ? 'Would you like to convert this item’s contents to a PDF and send it to the recipient?'
                : 'Would you like to send this item’s contents to the recipient?'}
            </Typography>

            <CollapsibleOptions>
              <TextField
                sx={{ width: '100%', mt: 1, mb: 2 }}
                label="Optional Comment"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <NotifyMailroomSwitch
                checked={notifyMailroom}
                onChange={() => setNotifyMailroom(!notifyMailroom)}
                label="Notify mailroom users about comment"
              />
              {isAdmin && (
                <Box sx={{ mt: 1 }}>
                  <NotifyMailroomSwitch
                    checked={skipNotifyRecipient}
                    onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
                    label="Skip notification to recipient about completed request"
                  />
                </Box>
              )}
            </CollapsibleOptions>
          </Box>
        }
        acceptButtonHandleClick={confirmFinishOpenAndScan}
        cancelButtonHandleClick={onActionFinish}
        acceptButtonTitle={isScan ? 'Convert & Send' : 'Send'}
        cancelButtonTitle={isScan ? 'No, Review Contents' : 'No, Not Now'}
      />
    </>
  );
};
